// Find a player by name or phone and pick them (F-SES-04): the start forms' first field — a PC's session and a
// console's booking (ADR-0034) both begin with it. Two characters search the organisation's players (the one player
// search, `usePlayerSearch`); a pick shows the player with "Change". A walk-in with no account cannot be made here:
// the api has no route that creates a player — they sign up at the kiosk or in the app (a follow-up).

import type { OrganizationPlayer } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Avatar, Button, Skeleton } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { SearchField } from '../../ui';
import { pickerResults, PLAYER_SEARCH_MIN, playerName, usePlayerSearch } from '../players/queries';

export function PlayerPicker({
  organizationId,
  player,
  onPick,
  label,
}: {
  readonly organizationId: string;
  readonly player: OrganizationPlayer | null;
  readonly onPick: (player: OrganizationPlayer | null) => void;
  /** The field's label; the start form's "Player" by default. */
  readonly label?: string;
}): ReactNode {
  const { t } = useTranslation('console');
  const [q, setQ] = useState('');
  const search = usePlayerSearch(organizationId, q, { enabled: q.trim().length >= PLAYER_SEARCH_MIN && player === null });
  const results = pickerResults(search, q);

  if (player !== null) {
    return (
      <div className="console-player-chosen">
        <Avatar name={playerName(player)} size="md" decorative />
        <span className="console-player-chosen__name">{playerName(player)}</span>
        <Button type="button" variant="ghost" size="sm" onClick={() => onPick(null)}>
          {t('sessions.start.change')}
        </Button>
      </div>
    );
  }
  return (
    <div className="console-player-pick">
      <SearchField label={label ?? t('sessions.start.player')} placeholder={t('sessions.start.playerHint')} value={q} onChange={(e) => setQ(e.target.value)} />
      {search.isFetching ? <Skeleton shape="text" lines={2} /> : null}
      {search.isSuccess && !search.isFetching && q.trim().length >= PLAYER_SEARCH_MIN && results.length === 0 ? <p className="console-subtle">{t('sessions.start.noMatch')}</p> : null}
      <ul className="console-player-pick__results">
        {results.map((candidate) => (
          <li key={candidate.id}>
            <button type="button" className="console-player-pick__result" onClick={() => onPick(candidate)}>
              <Avatar name={playerName(candidate)} size="sm" decorative />
              <span className="console-player-pick__name">{playerName(candidate)}</span>
              {candidate.phoneNumber === null ? null : <span className="console-subtle">{candidate.phoneNumber}</span>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
